"use client"

import { Dialog, DialogBackdrop, DialogPanel, Transition } from "@headlessui/react"
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react"
import { useGlobalAside, type GlobalAsideKind } from "../../context/global-aside-context"
import styles from "./aside.module.css"

const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not(:disabled):not([tabindex="-1"])',
  'input:not(:disabled):not([tabindex="-1"])',
  'select:not(:disabled):not([tabindex="-1"])',
  'textarea:not(:disabled):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(",")

const focusableElements = (panel: HTMLElement) =>
  Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.hidden && element.getAttribute("aria-hidden") !== "true"
  )

export default function Aside({
  kind,
  label,
  children,
  overlayTestId,
  labelledBy,
  side = "right",
}: {
  kind: GlobalAsideKind
  label: string
  children: ReactNode
  overlayTestId?: string
  labelledBy?: string
  side?: "left" | "right"
}) {
  const { close, isOpen } = useGlobalAside()
  const open = isOpen(kind)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return
    queueMicrotask(() => {
      const target = focusableElements(panel)[0] ?? panel
      target.focus({ preventScroll: true })
    })
  }, [open])

  const trapTab = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return
    const panel = panelRef.current
    if (!panel) return
    const focusable = focusableElements(panel)
    if (!focusable.length) {
      event.preventDefault()
      panel.focus({ preventScroll: true })
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const current = document.activeElement
    if (event.shiftKey && (current === first || !panel.contains(current))) {
      event.preventDefault()
      last.focus({ preventScroll: true })
    } else if (!event.shiftKey && (current === last || !panel.contains(current))) {
      event.preventDefault()
      first.focus({ preventScroll: true })
    }
  }

  return (
    <Transition show={open} appear unmount={false}>
      <Dialog
        open={open}
        onClose={() => close(kind)}
        static
        __demoMode
        aria-hidden={!open}
        inert={!open ? true : undefined}
        className={styles.dialog}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        data-testid={`global-aside-${kind}`}
      >
        <DialogBackdrop
          transition
          className={styles.overlay}
          data-testid={overlayTestId}
        />
        <DialogPanel
          ref={panelRef}
          transition
          className={styles.aside}
          data-side={side}
          tabIndex={-1}
          onKeyDown={trapTab}
        >
          {children}
        </DialogPanel>
      </Dialog>
    </Transition>
  )
}
